import React, { Fragment, lazy, Suspense, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Card, Step, StepLabel, Stepper, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import CatalogueAPI from 'api/orbitV2/CatalogueAPI'
import { useFormik } from 'formik'
import * as yup from 'yup'

import OrderAPI from '../../api/orbitV1/OrderAPI'
import OrgAPI from '../../api/orbitV1/OrgAPI'
import UenAPI from '../../api/orbitV1/UenAPI'
import bgImage from '../../assets/brand/orbit/orbitBG.jpg'
import LayoutSkeleton from '../../common/Skeletons/LayoutSkeleton'
import useDebounce from '../../hooks/useDebounce'
import { PaymentModal } from '../Payment/components'

const ContactDetails = lazy(() => import('./components/ContactDetails'))
const CompanyDetails = lazy(() => import('./components/CompanyDetails'))
const PlanOptions = lazy(
  () => import('../Orders/Client/NewOrder/components/PlanOptions')
)
const OnboardReview = lazy(() => import('./components/OnboardReview'))

export default function OnboardingPage() {
  /* Formik variables */
  const validationSchema = yup.object({
    contactName: yup.string().required('Contact Name is required'),
    contactNumber: yup
      .string()
      .matches(/^([689])\d{7}$/, 'Invalid Contact Number')
      .required('Contact Number is required'),
    email: yup
      .string()
      .email('Enter a valid email')
      .required('Email is required'),
    companyName: yup.string().required('Company name is required'),
    uen: yup
      .string()
      .matches(
        /^(S|T)([\d]{2})([A-Z]{2})([\d]{4})([A-Z])$|^[0-9]{9}[A-Z]$|^[0-9]{8}[A-Z]$/g,
/*
        /^\d{8}[A-Z]$/ || /^\d{9}[A-Z]$/ || /^[A-Z]\d{2}[A-Z]{2}\d{4}[A-Z]$/,
*/
        'Invalid UEN'
      )
      .required('UEN is required'),
    deliveryAddress: yup
      .object()
      .shape({
        blockNo: yup.string(),
        streetName: yup.string(),
        buildingName: yup.string(),
        postalCode: yup.string().matches(/^\d{6}$/, 'Invalid Postal Code'),
        unitNo: yup.string().required('Unit number is required')
      })
      .required('Delivery address is required'),
    billingAddress: yup.object().shape({
      blockNo: yup.string(),
      streetName: yup.string(),
      buildingName: yup.string(),
      postalCode: yup.string(),
      unitNo: yup.string()
    })
  })

  const formik = useFormik({
    initialValues: {
      contactName: '',
      contactNumber: '',
      email: '',
      companyName: '',
      uen: '',
      deliveryAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      },
      billingAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      }
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const navigate = useNavigate()
  const PLANS_LIMIT = 4
  const [page, setPage] = useState(0)
  const [paymentPageNo, setPaymentPageNo] = useState(0)
  const [plans, setPlans] = useState([
    {
      _id: '',
      productNname: '',
      baseplan: {
        price: { prefix: '$', value: '0', currency: 'SGD' },
        data: 0,
        voice: 0,
        sms: 0
      },
      addons: [
        {
          addonName: '',
          data: 0,
          voice: 0,
          sms: 0,
          price: { prefix: '$', value: '0', currency: 'SGD' }
        }
      ]
    }
  ])
  const [planId, setPlanId] = useState(0)
  const addOnPrice = 0
  const [totalPrice, setTotalPrice] = useState(0)
  const [numberOfSims, setNumberOfSims] = useState(1)
  const numberOfSimsOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

  /* Company Details Variables and error states */

  const [orgNameSearchResults, setOrgNameSearchResults] = useState([])
  const [isOrgSearching, setIsOrgSearching] = useState(false)
  const [isAddrSearching, setIsAddrSearching] = useState(false)

  /* Credit Card Variables */

  const PAYMENT_METHODS = {
    CREDIT_CARD: 'Credit Card',
    BANK_TRANSFER: 'Bank Transfer',
    PAYNOW: 'PayNow'
  }
  const [paymentMethod, setPaymentMethod] = useState(
    PAYMENT_METHODS.CREDIT_CARD
  )

  /* Delivery Address Variables and error states */

  const [deliveryAddrSearch, setDeliveryAddrSearch] = useState('')
  const [deliveryAddrSearchResults, setDeliveryAddrSearchResults] = useState([])

  /* Billing Address Variables and error states */

  const [billingAddrSearch, setBillingAddrSearch] = useState('')
  const [billingAddrSearchResults, setBillingAddrSearchResults] = useState([])
  const [isBillingAddrSame, setIsBillingAddrSame] = useState(false)

  /* Review Order Variables and error states */

  const [unexpectedError, setUnexpectedError] = useState('')
  const [hasCreated, setHasCreated] = useState(false)
  const [orderMetaInfo, setOrderMetaInfo] = useState({})

  /* Dialog States */

  const [isDialogOpen, setDialogOpen] = useState(false)

  /* useDebounce hook */
  const debouncedCompanyNameSearch = useDebounce(
    formik.values.companyName,
    1000
  )
  const debouncedUENSearch = useDebounce(
    formik.values.uen,
    1000
  )
  const debouncedDeliveryAddressSearch = useDebounce(deliveryAddrSearch, 1000)
  const debouncedBillingAddressSearch = useDebounce(billingAddrSearch, 1000)

  /* function to search for company name */

  const handleCompanyNameSearch = async (search) => {
    try {
      const companySearch = await UenAPI.companyNameSearch(search)
      if (companySearch.data) {
        setIsOrgSearching(false)
        setOrgNameSearchResults(companySearch.data)
      }
    } catch (error) {
      toast.error('Error retrieving company name')
    }
  }

  /* function to search for uen */

  const handleUENSearch = async (search) => {
    try {
      const companySearch = await UenAPI.companyUENSearch(search)
      if (companySearch.data) {
        setIsOrgSearching(false)
        setOrgNameSearchResults(companySearch.data)
      }
    } catch (error) {
      toast.error('Error retrieving uen')
    }
  }

  /* function to search for delivery address */

  const handleDeliveryAddressSearch = async (deliverySearch) => {
    try {
      const deliveryAddressSearch = await OrgAPI.getAddress(deliverySearch)
      if (deliveryAddressSearch.data) {
        setIsAddrSearching(false)
        setDeliveryAddrSearchResults(deliveryAddressSearch.data)
      }
    } catch (error) {
      toast.error('Error fetching address')
    }
  }

  /* function to search for billing address */

  const handleBillingAddressSearch = async (billingSearch) => {
    try {
      const billingAddressSearch = await OrgAPI.getAddress(billingSearch)
      if (billingAddressSearch.data) {
        setIsAddrSearching(false)
        setBillingAddrSearchResults(billingAddressSearch.data)
      }
    } catch (error) {
      toast.error('Error fetching address')
    }
  }

  useEffect(() => {
    if (debouncedDeliveryAddressSearch) {
      setIsAddrSearching(true)
      handleDeliveryAddressSearch(debouncedDeliveryAddressSearch)
    }

    if (debouncedBillingAddressSearch) {
      setIsAddrSearching(true)
      handleBillingAddressSearch(debouncedBillingAddressSearch)
    }

    if (debouncedCompanyNameSearch) {
      setIsOrgSearching(true)
      handleCompanyNameSearch(debouncedCompanyNameSearch)
    }

    if (debouncedUENSearch) {
      setIsOrgSearching(true)
      handleUENSearch(debouncedUENSearch)
    }
  }, [
    debouncedDeliveryAddressSearch,
    debouncedBillingAddressSearch,
    debouncedCompanyNameSearch,
    debouncedUENSearch
  ])

  // Capitalise only first letter of each word assuming input is all caps

  const capitaliseFirst = (str) => {
    const strArray = str.split(' ')
    let result = ''
    for (const word of strArray) {
      result = result + ' ' + word[0] + word.slice(1).toLowerCase()
    }
    return result.slice(1)
  }

  useEffect(() => {
    setTotalPrice(
      plans[planId].baseplan.price.value * numberOfSims + addOnPrice
    )
  }, [plans, planId, numberOfSims])

  const { data: retrieveProducts, error: retrieveProductsError } = useQuery(
    ['getProducts'],
    async () => {
      const { data } = await CatalogueAPI.getAllProducts(PLANS_LIMIT)
      return data
    }
  )

  if (retrieveProductsError) {
    toast.error('Error retrieving products. Please try again later!')
  }

  useEffect(() => {
    if (retrieveProducts) {
      setPlans(retrieveProducts)
    }
  }, [retrieveProducts])

  const retrieveAddress = async (postal) => {
    try {
      const addressByPostal = await OrgAPI.getAddress(postal)

      const addrDetails = addressByPostal.data[0]
      formik.setFieldValue('deliveryAddress.blockNo', addrDetails.BLK_NO)
      formik.setFieldValue('deliveryAddress.streetName', addrDetails.ROAD_NAME)
      formik.setFieldValue(
        'deliveryAddress.buildingName',
        addrDetails.BUILDING ? addrDetails.BUILDING : ''
      )
      formik.setFieldValue('deliveryAddress.postalCode', addrDetails.POSTAL)
      formik.setFieldValue('billingAddress.blockNo', addrDetails.BLK_NO)
      formik.setFieldValue('billingAddress.streetName', addrDetails.ROAD_NAME)
      formik.setFieldValue(
        'billingAddress.buildingName',
        addrDetails.BUILDING ? addrDetails.BUILDING : ''
      )
      formik.setFieldValue('billingAddress.postalCode', addrDetails.POSTAL)
    } catch (error) {
      toast.error('Error retrieving address')
    }
  }

  /* Function to create Org + User + Order */
  const handleCreate = async () => {
    const orderDetails = {
      newUser: true,
      uen: formik.values.uen,
      orgName: formik.values.companyName,
      contactName: formik.values.contactName,
      contactNumber: formik.values.contactNumber,
      type: 'order',
      email: formik.values.email,
      deliveryAddress: formik.values.deliveryAddress,
      billingAddress: formik.values.billingAddress,
      plan: plans[planId]._id,
      planName: plans[planId].name,
      numberOfSims: parseInt(numberOfSims),
      price: totalPrice,
      paymentMethod,
      itemName: `${numberOfSims} ${plans[planId].name} SIMs` // for credit card
    }
    try {
      if (!hasCreated) {
        const metaInfo = await OrderAPI.createOrder(orderDetails)
        metaInfo.data['email'] = orderDetails.email
        setHasCreated(true)
        setOrderMetaInfo(metaInfo)
      }
    } catch (error) {
      toast.error('Error creating order.')
    }
  }

  /* Submit Function */

  const handleSubmit = async () => {
    setUnexpectedError('')

    // Call backend to create stripe client session for payment
    try {
      if (paymentMethod === PAYMENT_METHODS.BANK_TRANSFER) {
        setDialogOpen(false)
        setPaymentPageNo(0)
        navigate('/order-success')
      }
    } catch (error) {
      toast.error('Error submitting payment!')
    }
  }

  const goNextPage = () => {
    setPage(page + 1)
  }

  const goPreviousPage = () => setPage(page - 1)

  const SELECT_PLAN = (
    <PlanOptions
      planId={planId}
      setPlanId={setPlanId}
      plans={plans}
      numberOfSims={numberOfSims}
      setNumberOfSims={setNumberOfSims}
      numberOfSimsOptions={numberOfSimsOptions}
      goNextPage={goNextPage}
    />
  )

  const COMPANY_DETAILS = (
    <CompanyDetails
      billingAddrSearch={billingAddrSearch}
      billingAddrSearchResults={billingAddrSearchResults}
      deliveryAddrSearch={deliveryAddrSearch}
      deliveryAddrSearchResults={deliveryAddrSearchResults}
      formik={formik}
      isAddrSearching={isAddrSearching}
      isBillingAddrSame={isBillingAddrSame}
      isOrgSearching={isOrgSearching}
      orgNameSearchResults={orgNameSearchResults}
      backHandler={goPreviousPage}
      retrieveAddress={retrieveAddress}
      nextHandler={goNextPage}
      setBillingAddrSearch={setBillingAddrSearch}
      setBillingAddrSearchResults={setBillingAddrSearchResults}
      setDeliveryAddrSearch={setDeliveryAddrSearch}
      setDeliveryAddrSearchResults={setDeliveryAddrSearchResults}
      setIsBillingAddrSame={setIsBillingAddrSame}
      variant='onboarding'
    />
  )

  const CONTACT_DETAILS = (
    <ContactDetails
      formik={formik}
      nextHandler={goNextPage}
      backHandler={goPreviousPage}
      variant='onboarding'
    />
  )

  /* Review Order Page */

  const reviewOrderDetails = {
    'Company Name': formik.values.companyName,
    'Unique Entity Number (UEN)': formik.values.uen,
    "Contact Person's Name": formik.values.contactName,
    'Contact Number': `+65 ${formik.values.contactNumber}`,
    Email: formik.values.email
  }

  const reviewOrderDelivery = {
    'Delivery Address': `${formik.values.deliveryAddress.blockNo} 
        ${capitaliseFirst(formik.values.deliveryAddress.streetName)}
        ${
          formik.values.deliveryAddress.unitNo
            ? formik.values.deliveryAddress.unitNo + ' '
            : ''
        }
        ${
          formik.values.deliveryAddress.buildingName !== 'NIL'
            ? capitaliseFirst(formik.values.deliveryAddress.buildingName) + ', '
            : ''
        }
       ${formik.values.deliveryAddress.postalCode}`,
    'Billing Address': `${formik.values.billingAddress.blockNo}
        ${capitaliseFirst(formik.values.billingAddress.streetName)}
        ${
          formik.values.billingAddress.unitNo
            ? formik.values.billingAddress.unitNo + ' '
            : ''
        }
        ${
          formik.values.billingAddress.buildingName !== 'NIL'
            ? capitaliseFirst(formik.values.billingAddress.buildingName) + ', '
            : ''
        }
        ${formik.values.billingAddress.postalCode}`
  }

  const REVIEW_ORDER = (
    <OnboardReview
      planId={planId}
      plans={plans}
      numberOfSims={numberOfSims}
      totalPrice={totalPrice}
      addOnPrice={addOnPrice}
      handleCreate={handleCreate}
      unexpectedError={unexpectedError}
      goPreviousPage={goPreviousPage}
      setDialogOpen={setDialogOpen}
    >
      <Typography variant='h5' sx={{ color: 'primary.light', marginTop: 2 }}>
        Company Details
      </Typography>

      {Object.entries(reviewOrderDetails).map(([label, orderFieldValue]) => {
        return (
          <Fragment key={label}>
            <Typography sx={{ marginBottom: 0, color: 'neutrals.500' }}>
              {label}
            </Typography>
            <Typography sx={{ marginTop: 0, marginBottom: '15px' }}>
              {orderFieldValue}
            </Typography>
          </Fragment>
        )
      })}

      <Typography sx={{ color: 'primary.light' }}>
        Delivery and Billing Details
      </Typography>

      {Object.entries(reviewOrderDelivery).map(
        ([label, deliveryFieldValue]) => {
          return (
            <Fragment key={label}>
              <Typography sx={{ marginBottom: 0, color: 'neutrals.500' }}>
                {label}
              </Typography>
              <Typography sx={{ marginTop: 0, marginBottom: 2 }}>
                {deliveryFieldValue}
              </Typography>
            </Fragment>
          )
        }
      )}
    </OnboardReview>
  )

  const steps = [
    { stepName: 'select-plan', label: 'Select Plan', content: SELECT_PLAN },
    {
      stepName: 'company-details',
      label: 'Company Details',
      content: COMPANY_DETAILS
    },
    {
      stepName: 'contact-details',
      label: 'Contact Details',
      content: CONTACT_DETAILS
    },
    { stepName: 'review-order', label: 'Review Order', content: REVIEW_ORDER }
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
        height: '100vh',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        overflowY: 'hidden',
        padding: '4em'
      }}
    >
      <Card>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '85vh',
            width: '60vw',
            padding: '3em 2rem',
            minWidth: '900px',
            overflowY: 'scroll'
          }}
        >
          {page < steps.length && (
            <Box sx={{ marginBottom: 2 }}>
              <Stepper
                activeStep={page}
                alternativeLabel
                sx={{ width: '55vw', minWidth: '825px', margin: 0 }}
              >
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}
          <Suspense fallback={<LayoutSkeleton />}>
            {steps[page].content}
          </Suspense>
        </Box>
      </Card>
      {isDialogOpen && (
        <PaymentModal
          setDialogOpen={setDialogOpen}
          isDialogOpen={isDialogOpen}
          amountPayable={totalPrice} // pass amount to pay into modal
          paymentPageNo={paymentPageNo}
          setPaymentPageNo={setPaymentPageNo}
          orgName={formik.values.companyName}
          // TODO: change to update state once payment has gone through
          handleSubmit={handleSubmit}
          metaInfo={orderMetaInfo}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          PAYMENT_METHODS={PAYMENT_METHODS}
          email={formik.values.email}
          variant='onboarding'
        />
      )}
    </Box>
  )
}
